import { AxiosRequestConfig, AxiosError } from "axios";
import Qs from "qs";
import { ApiResponse, ResponseStatus, ResponseCode } from "@types";
import instance from "./axios";
import { fetchAuthSession } from "aws-amplify/auth";
import { clog } from "../logs/clog";
import { redirectAuth } from "./redirectAuth";

const getApi = async <P, T = unknown>(
  url: string,
  values?: T,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<P>> => {
  try {
    const reqUrl = `${url}`;
    const option = {
      params: values || {},
      paramsSerializer: function (params: T | unknown) {
        return Qs.stringify(params, { skipNulls: true });
      },
    };

    const session = await fetchAuthSession({ forceRefresh: true });
    const authorizationHeader = session.tokens?.idToken;

    if (!authorizationHeader) {
      redirectAuth();
      return {
        status: ResponseStatus.FORBIDDEN,
        statusCode: ResponseCode.FORBIDDEN,
        message: "Unauthorized access. Please log in.",
      };
    }

    const configWithHeaders = config
      ? {
          ...config,
          headers: {
            Authorization: `Bearer ${authorizationHeader}`,
          },
          ...option,
        }
      : {
          headers: {
            Authorization: `Bearer ${authorizationHeader}`,
          },
          ...option,
        };

    const response = await instance.get<ApiResponse<P>>(
      reqUrl,
      configWithHeaders,
    );

    if (response.data.status === ResponseStatus.SUCCESS) {
      return response.data;
    }

    throw new AxiosError<ApiResponse<P>>(
      response.data.message,
      response.data.status || ResponseStatus.ERROR,
      undefined,
      undefined,
      response,
    );
  } catch (e) {
    const errorResponse = e as unknown as AxiosError<ApiResponse<unknown>>;
    clog("errorResponse", errorResponse);
    if (errorResponse.response?.status === ResponseCode.FORBIDDEN) {
      redirectAuth();
      return {
        status: ResponseStatus.FORBIDDEN,
        statusCode: ResponseCode.FORBIDDEN,
        message: "Unauthorized access. Please log in.",
      };
    }
    return {
      status: errorResponse.response?.data?.status || ResponseStatus.ERROR,
      statusCode:
        errorResponse.response?.data?.statusCode || ResponseCode.SERVER_ERROR,
      message: errorResponse.response?.data?.message || "An error occurred",
    };
  }
};

export default getApi;
